import { useState } from 'react';
import { Loader2, Play, Search, FileText, Eye, CheckCircle, Filter } from 'lucide-react';
import ContractsDashboardFilters from './ContractsDashboardFilters';
import ContractDetailModal from './ContractDetailModal';
import { useContractsStore } from '../../store/contracts.store';

const CHUNG_TU_OPTIONS = [
  { value: 'Biên nhận', label: 'Biên nhận', color: 'blue' },
  { value: 'Biên lai thu tiền', label: 'Biên lai thu tiền', color: 'green' },
  { value: 'Hợp đồng', label: 'Hợp đồng', color: 'purple' },
  { value: 'CCCD', label: 'CCCD', color: 'orange' },
  { value: 'Lưu trữ hợp đồng', label: 'Lưu trữ hợp đồng', color: 'cyan' },
];

interface Props {
  isDark: boolean;
  theme: Record<string, string>;
}

function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US');
}

function ChungTuBadges({ value, isDark }: { value: string; isDark: boolean }) {
  const items = value.split(';').filter(Boolean);
  const colorMap: Record<string, string> = {
    'Biên nhận': isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700',
    'Biên lai thu tiền': isDark ? 'bg-green-500/15 text-green-300' : 'bg-green-50 text-green-700',
    'Hợp đồng': isDark ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-50 text-purple-700',
    'CCCD': isDark ? 'bg-orange-500/15 text-orange-300' : 'bg-orange-50 text-orange-700',
    'Lưu trữ hợp đồng': isDark ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-50 text-cyan-700',
  };

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span key={item} className={`px-1.5 py-0.5 text-[10px] rounded-full whitespace-nowrap ${colorMap[item] || (isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600')}`}>
          {item}
        </span>
      ))}
    </div>
  );
}

export default function ContractsDashboardContent({ isDark }: Props) {
  const {
    isLoading, isLoadingFiles, error,
    deals, selectedDeal, fetchedKey,
    reportFrom, reportTo, searchQuery, chungTuFilter,
    setSearchQuery, toggleChungTuFilter, fetchDealFiles, clearSelectedDeal,
  } = useContractsStore();

  const [viewingDealId, setViewingDealId] = useState<string | null>(null);
  const hasFetched = fetchedKey !== null;

  const filteredDeals = deals.filter(d => {
    // Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!d.dealname.toLowerCase().includes(q) &&
          !d.deal_by_studio.toLowerCase().includes(q) &&
          !d.deal_phone.includes(searchQuery)) {
        return false;
      }
    }
    // Chứng từ filter — deal must have ALL selected types
    if (chungTuFilter.length > 0) {
      return chungTuFilter.every(ct => d.kiem_tra_chung_tu.includes(ct));
    }
    return true;
  });

  const handleViewDeal = async (dealId: string) => {
    setViewingDealId(dealId);
    await fetchDealFiles(dealId);
  };

  const handleCloseModal = () => {
    setViewingDealId(null);
    clearSelectedDeal();
  };

  return (
    <>
      <ContractsDashboardFilters isDark={isDark} />

      {error && (
        <div className={`mt-4 p-4 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      )}

      {!isLoading && !hasFetched && (
        <div className="mt-8 flex flex-col items-center justify-center py-20 text-center">
          <Play className={`w-12 h-12 ${isDark ? 'text-slate-600' : 'text-gray-400'} mb-4`} />
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Chọn khoảng thời gian và nhấn <span className="text-cyan-400 font-medium">"Chạy report"</span> để xem hợp đồng
          </p>
        </div>
      )}

      {!isLoading && hasFetched && (
        <div className="mt-4 sm:mt-6 space-y-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Hợp đồng{' '}
              <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>
                ({reportFrom} — {reportTo})
              </span>
              <span className={`ml-2 text-sm font-normal ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                {filteredDeals.length} deal{filteredDeals.length !== deals.length ? ` / ${deals.length}` : ''}
              </span>
            </h2>

            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm theo tên, studio, SĐT..."
                className={`pl-10 pr-4 py-2 border rounded-lg text-sm w-full sm:w-72 ${isDark ? 'bg-white/[0.03] border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'}`}
              />
            </div>
          </div>

          {/* Chứng từ Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              <Filter className="w-3.5 h-3.5" />
              Chứng từ:
            </span>
            {CHUNG_TU_OPTIONS.map((opt) => {
              const active = chungTuFilter.includes(opt.value);
              const count = deals.filter(d => d.kiem_tra_chung_tu.includes(opt.value)).length;
              const colorClasses: Record<string, { active: string; inactive: string }> = {
                blue: {
                  active: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
                  inactive: isDark ? 'bg-white/[0.03] border-white/10 text-white/40' : 'bg-gray-50 border-gray-200 text-gray-500',
                },
                green: {
                  active: 'bg-green-500/20 border-green-500/50 text-green-300',
                  inactive: isDark ? 'bg-white/[0.03] border-white/10 text-white/40' : 'bg-gray-50 border-gray-200 text-gray-500',
                },
                purple: {
                  active: 'bg-purple-500/20 border-purple-500/50 text-purple-300',
                  inactive: isDark ? 'bg-white/[0.03] border-white/10 text-white/40' : 'bg-gray-50 border-gray-200 text-gray-500',
                },
                orange: {
                  active: 'bg-orange-500/20 border-orange-500/50 text-orange-300',
                  inactive: isDark ? 'bg-white/[0.03] border-white/10 text-white/40' : 'bg-gray-50 border-gray-200 text-gray-500',
                },
                cyan: {
                  active: 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300',
                  inactive: isDark ? 'bg-white/[0.03] border-white/10 text-white/40' : 'bg-gray-50 border-gray-200 text-gray-500',
                },
              };
              const cls = colorClasses[opt.color] || colorClasses.blue;
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleChungTuFilter(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors hover:opacity-80 ${active ? cls.active : cls.inactive}`}
                >
                  {opt.label} ({count})
                </button>
              );
            })}
            {chungTuFilter.length > 0 && (
              <button
                onClick={() => chungTuFilter.forEach(ct => toggleChungTuFilter(ct))}
                className={`px-2 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Xóa bộ lọc
              </button>
            )}
          </div>

          {/* Table */}
          <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>STT</th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Khách hàng</th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Studio</th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Ngày close</th>
                    <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Số tiền</th>
                    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Chứng từ</th>
                    <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Xem</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-gray-100'}`}>
                  {filteredDeals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {searchQuery ? 'Không tìm thấy kết quả' : 'Không có deal nào có chứng từ trong khoảng thời gian này'}
                      </td>
                    </tr>
                  ) : (
                    filteredDeals.map((deal, idx) => {
                      const hasContract = deal.kiem_tra_chung_tu.includes('Hợp đồng');
                      return (
                        <tr
                          key={deal.id}
                          className={`transition-colors cursor-pointer ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'}`}
                          onClick={() => handleViewDeal(deal.id)}
                        >
                          <td className={`px-4 py-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{idx + 1}</td>
                          <td className={`px-4 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <div className="flex items-center gap-2">
                              {deal.dealname}
                              {hasContract && <FileText className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />}
                            </div>
                            {deal.deal_phone && (
                              <div className={`text-xs mt-0.5 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{deal.deal_phone}</div>
                            )}
                          </td>
                          <td className={`px-4 py-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {deal.deal_by_studio.replace('Studio ', '')}
                          </td>
                          <td className={`px-4 py-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {deal.closedate ? new Date(deal.closedate).toLocaleDateString('vi-VN') : '-'}
                          </td>
                          <td className={`px-4 py-3 text-right tabular-nums ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {formatAmount(deal.amount)}
                          </td>
                          <td className="px-4 py-3">
                            <ChungTuBadges value={deal.kiem_tra_chung_tu} isDark={isDark} />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewDeal(deal.id); }}
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-cyan-400' : 'hover:bg-cyan-50 text-cyan-600'}`}
                              title="Xem chi tiết"
                            >
                              {viewingDealId === deal.id && isLoadingFiles
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Eye className="w-4 h-4" />
                              }
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-purple-400" />
              {deals.filter(d => d.kiem_tra_chung_tu.includes('Hợp đồng')).length} deal có hợp đồng
            </span>
            <span>
              Tổng: {formatAmount(deals.reduce((sum, d) => sum + d.amount, 0))} VND
            </span>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDeal && (
        <ContractDetailModal
          data={selectedDeal}
          isDark={isDark}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
